import React, { useEffect, useState, useRef, useCallback } from "react";
import { Modal, Button, Layout } from "antd";
import styles from "./showModal.module.less";
import { useHandle } from "../../task/hook/useHandle";
import { useSelector } from "react-redux";
import { RootState } from "../../../redux/rootReducers";
import { setBackendClearObtenerEstadoUsuario } from "../../../redux/slices/backend/obtenerEstadoUsuario";
import { useCustomDispatch } from "../../../hooks/redux";
import { LazySuspense } from "./lazySuspense";
import ModalMsgCustoms from "../../iu/modalMsgCuston/modalMsgCuston";

const { Header } = Layout;

interface UseIdleTimeoutProps {
  timeOutEstadoUsuario: number;
  timeOutModal: number;
  onLogout: () => void;
  isLogin: boolean;
}

// 📌 CONSTANTES DE SINCRONIZACIÓN
const KEY_ACTIVIDAD = "pestaña_actividad_global";
const KEY_SYNC_MODAL = "sync_close_modal_timestamp";

// 📌 LLAVES DE SESIÓN (Ajusta estos nombres si en tu localStorage se llaman distinto)
const KEY_SESSION_ID = "IdSesion";
const KEY_API_TOKEN = "TokenApi";

const UseIdleTimeoutEstadoUsuario = ({
  timeOutEstadoUsuario,
  timeOutModal,
  onLogout,
  isLogin,
}: UseIdleTimeoutProps) => {
  const dispatchs = useCustomDispatch();
  const {
    handleBackendObtenerEstadoUsuario,
    handleBackendClearObtenerEstadoUsuario,
    handleDisponible,
  } = useHandle();

  const [showModal, setShowModal] = useState<boolean>(false);
  const [timeLeft, setTimeLeft] = useState<number>(
    Math.floor(timeOutModal / 1000),
  );
  const [isHandlingStayLoggedIn, setIsHandlingStayLoggedIn] = useState(false);

  // REFS para persistencia
  const heartbeatIntervalRef = useRef<any>(null);
  const countdownIntervalRef = useRef<any>(null);
  const throttleRef = useRef<number>(0);
  const onLogoutRef = useRef(onLogout);
  const showModalRef = useRef(showModal);
  const lastShiftRefreshHourRef = useRef<number>(-1);
  const isShiftRefreshRef = useRef<boolean>(false); // Mantengo el nombre para minimizar cambios, pero cambio su semántica a "skipInactivityCheck"
  const pendingInactivityModalCheckRef = useRef<boolean>(false);

  useEffect(() => {
    onLogoutRef.current = onLogout;
  }, [onLogout]);
  useEffect(() => {
    showModalRef.current = showModal;
  }, [showModal]);

  // --- 0. SINCRONIZACIÓN INICIAL AL MONTAR ---
  // Al abrir el navegador o refrescar, traemos el estado real inmediatamente.
  useEffect(() => {
    if (isLogin) {
      handleBackendObtenerEstadoUsuario();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Solo al montar

  const { obtenerEstadoUsuario, status } = useSelector(
    (store: RootState) => store.obtenerEstadoUsuario,
  );

  const { isLoading, Mensaje } = useSelector(
    (store: RootState) => store.actualizarDisponibilidad,
  );
  const { HoraFinNormal, HoraFinNoNormal } = useSelector(
    (store: RootState) => store.ponderacionesUsuarios,
  );

  // --- 1. LÓGICA DE CHEQUEO (HEARTBEAT) ---
  // En lugar de un setTimeout largo, revisamos el timestamp cada 5 segundos.
  // Esto es inmune a los re-renders de las notificaciones.
  const checkGlobalInactivity = useCallback(async () => {
    if (!isLogin || showModalRef.current) return;

    const lastGlobal = Number(localStorage.getItem(KEY_ACTIVIDAD) || 0);
    const now = new Date();
    const nowMs = now.getTime();
    const elapsed = nowMs - lastGlobal;

    if (elapsed >= timeOutEstadoUsuario) {
      pendingInactivityModalCheckRef.current = true;
      await handleBackendObtenerEstadoUsuario();
    }

    // --- LÓGICA DE REFRESCO POR CAMBIO DE TURNO ---
    const currentHour = now.getHours();
    const currentMinutes = now.getMinutes();
    const currentSeconds = now.getSeconds();

    const isShiftEndReached = (targetHour: number | null) => {
      if (targetHour === null) return false;
      const normalizedTarget = targetHour === 24 ? 0 : targetHour;
      // Disparamos si estamos en el primer minuto del cambio de hora, pasados los 10 segundos
      return (
        currentHour === normalizedTarget &&
        currentMinutes === 0 &&
        currentSeconds >= 10
      );
    };

    if (
      isShiftEndReached(HoraFinNormal) ||
      isShiftEndReached(HoraFinNoNormal)
    ) {
      if (lastShiftRefreshHourRef.current !== currentHour) {
        isShiftRefreshRef.current = true;
        // Al terminar HoraFinNormal → entra en FDH → botón de guardia visible
        // Al terminar HoraFinNoNormal → regresa a HN → botón de guardia oculto
        if (isShiftEndReached(HoraFinNoNormal)) {
          sessionStorage.setItem("mostrarBotonGuardia", "false");
          await handleDisponible(true, false);
        } else {
          sessionStorage.setItem("mostrarBotonGuardia", "true");
        }
        window.dispatchEvent(new Event("cambioFranjaHoraria"));
        await handleBackendObtenerEstadoUsuario();
        lastShiftRefreshHourRef.current = currentHour;
      }
    } else {
      // Si ya no estamos en el segundo/minuto del trigger, reseteamos la ref de la hora
      // para permitir el trigger de la próxima hora si fuera igual (aunque es improbable que HoraFinNormal == HoraFinNoNormal e.g. 18 y 18)
      // O simplemente reseteamos si la hora cambió y ya no estamos en la ventana de trigger
      if (
        lastShiftRefreshHourRef.current !== currentHour &&
        currentMinutes > 0
      ) {
        lastShiftRefreshHourRef.current = -1;
      }
    }
  }, [
    isLogin,
    timeOutEstadoUsuario,
    handleBackendObtenerEstadoUsuario,
    isLoading,
    HoraFinNormal,
    HoraFinNoNormal,
  ]);

  // --- 2. REACCIONAR A REDUX (APERTURA) ---
  useEffect(() => {
    if (status === "succeeded" && obtenerEstadoUsuario?.length > 0) {
      const estado = obtenerEstadoUsuario[0];

      // 1. Sincronización de la bandera de visibilidad (Siempre se ejecuta)
      if (estado.TipoHorario === "FDH") {
        sessionStorage.setItem("mostrarBotonGuardia", "true");
      } else {
        sessionStorage.setItem("mostrarBotonGuardia", "false");
      }
      window.dispatchEvent(new Event("cambioFranjaHoraria"));

      // 2. Control del Modal de Inactividad
      // Solo evaluamos el modal si la consulta fue disparada por inactividad real (Heartbeat)
      if (pendingInactivityModalCheckRef.current) {
        pendingInactivityModalCheckRef.current = false;

        // El modal debe renderizarse si el usuario está en guardia y liberado en FDH
        if (
          estado.GuardiaActiva &&
          estado.EstadoUsuario === "Liberado" &&
          estado.TipoHorario === "FDH" &&
          !showModalRef.current
        ) {
          setTimeLeft(Math.floor(timeOutModal / 1000));
          setShowModal(true);
        }
      } else {
        // Si fue una sincronización automática (Mount o Shift), limpiamos cualquier flag residual
        isShiftRefreshRef.current = false;
      }
    }
  }, [status, obtenerEstadoUsuario, timeOutModal]);

  // --- 3. CUENTA REGRESIVA DEL MODAL ---
  useEffect(() => {
    if (showModal && isLogin) {
      if (countdownIntervalRef.current)
        clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = setInterval(() => {
        setTimeLeft((prev) => {
          if (prev <= 1) {
            clearInterval(countdownIntervalRef.current);
            onLogoutRef.current();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => {
      if (countdownIntervalRef.current)
        clearInterval(countdownIntervalRef.current);
    };
  }, [showModal, isLogin]);

  // --- 4. EVENTOS Y SINCRONIZACIÓN ---
  useEffect(() => {
    if (!isLogin) return;

    // Actualizar actividad (Solo por acciones del usuario)
    const handleUserActivity = () => {
      if (showModalRef.current) return;
      const now = Date.now();
      if (now - throttleRef.current > 1000) {
        throttleRef.current = now;
        localStorage.setItem(KEY_ACTIVIDAD, now.toString());
      }
    };

    const handleStorageChange = (e: StorageEvent) => {
      // Logout Global
      if (
        (e.key === KEY_SESSION_ID || e.key === KEY_API_TOKEN) &&
        !e.newValue
      ) {
        onLogoutRef.current();
        return;
      }
      // Cierre Espejo por actividad o por botón
      if ((e.key === KEY_ACTIVIDAD || e.key === KEY_SYNC_MODAL) && e.newValue) {
        if (showModalRef.current) setShowModal(false);
      }
    };

    // Listeners de usuario
    const events = [
      "mousemove",
      "mousedown",
      "keypress",
      "scroll",
      "touchstart",
    ];
    events.forEach((ev) => window.addEventListener(ev, handleUserActivity));
    window.addEventListener("storage", handleStorageChange);

    // Iniciar el Heartbeat (revisar cada 5 segundos)
    heartbeatIntervalRef.current = setInterval(checkGlobalInactivity, 5000);

    return () => {
      events.forEach((ev) =>
        window.removeEventListener(ev, handleUserActivity),
      );
      window.removeEventListener("storage", handleStorageChange);
      if (heartbeatIntervalRef.current)
        clearInterval(heartbeatIntervalRef.current);
    };
  }, [isLogin, checkGlobalInactivity]);

  // --- 5. ACCIÓN BOTÓN ---
  const handleStayLoggedIn = () => {
    setIsHandlingStayLoggedIn(true);
    localStorage.setItem(KEY_SYNC_MODAL, Date.now().toString());
    setShowModal(false);
    dispatchs(setBackendClearObtenerEstadoUsuario());
    handleDisponible(true, false);
    handleBackendClearObtenerEstadoUsuario();

    setTimeout(() => {
      setIsHandlingStayLoggedIn(false);
      window.location.reload();
    }, 6000);
  };

  if (!isLogin) return null;
  return (
    <>
      <LazySuspense>
        <ModalMsgCustoms msjMsj={Mensaje + ""} isVisible={isLoading} />
      </LazySuspense>

      <Modal
        title="Sesión a punto de expirar"
        style={{ height: "80%" }}
        open={showModal}
        onCancel={() => {}}
        closable={false}
        footer={null}
        maskClosable={false}
      >
        <div className={styles.header}>
          <Header style={{ backgroundColor: "#fff" }}>
            <div style={{ color: "brown" }}>
              En <b>{timeLeft} segundos</b> serás deslogueado.
              <Button
                style={{ color: "brown" }}
                type="ghost"
                onClick={handleStayLoggedIn}
                disabled={isHandlingStayLoggedIn}
              >
                Permanecer Logueado
              </Button>
            </div>
          </Header>
          <br />
        </div>
      </Modal>
    </>
  );
};

export default UseIdleTimeoutEstadoUsuario;
